import React from 'react';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/system';
import PlusIcon from 'assets/icons/icon-plus-box-outline.svg?react';
import MinusIcon from 'assets/icons/icon-minus-box-outline.svg?react';
import { getLanguage } from 'translation';
import professionCases from 'helpers/professionCases';

const RISK_LEVELS = {
  minimal:     { score: 10, color: '#4f9a04' },
  low:         { score: 25, color: '#5fa510' },
  medium:      { score: 50, color: '#d5921b' },
  moderate:    { score: 50, color: '#d5921b' },
  high:        { score: 75, color: '#e25510' },
  'very-high': { score: 90, color: '#b21625' },
  critical:    { score: 95, color: '#8b0000' },
  extreme:     { score: 95, color: '#8b0000' },
};

const INT_TO_KEY = { 1: 'minimal', 2: 'low', 3: 'medium', 4: 'high', 5: 'very-high' };

const Section = styled('section')(({ theme }) => ({
  borderRadius: 15,
  // Neutral border so the meaning-carrying color sits inside (the RiskPill
  // accent + meter fill), not on the outer chrome. Previously the green
  // border conflicted with the pill semantics ("high risk" inside a green
  // frame read as a contradiction).
  border: `1px solid ${theme.palette.grey[100]}`,
  backgroundColor: theme.palette.white.main,
  marginBottom: 25,
}));

const HeaderRow = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 16,
  padding: '11px 25px 10px',
  borderBottom: '1px solid #d6d6d6',
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
}));

const HeaderTitle = styled(Typography)(({ theme }) => ({
  fontSize: 24,
  margin: 0,
  [theme.breakpoints.down('md')]: {
    lineHeight: '34px',
  },
}));

const RiskPill = styled('span')(({ accent }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  padding: '6px 14px',
  borderRadius: 100,
  border: `1px solid ${accent}`,
  color: accent,
  fontSize: 14,
  fontWeight: 500,
  whiteSpace: 'nowrap',
  flexShrink: 0,
  fontFamily: 'Rubik-Regular',
}));

const PillDot = styled('span')(({ accent }) => ({
  width: 8,
  height: 8,
  borderRadius: '50%',
  backgroundColor: accent,
  display: 'inline-block',
}));

const Content = styled('div')(({ theme }) => ({
  padding: '20px 25px 24px 25px',
  [theme.breakpoints.down('sm')]: {
    padding: '16px 20px 20px 20px',
  },
}));

const MeterBlock = styled('div')(() => ({
  marginBottom: 20,
}));

const MeterLabelRow = styled('div')(() => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'baseline',
  marginBottom: 8,
}));

const MeterLabel = styled(Typography)(({ theme }) => ({
  fontSize: 16,
  color: theme.palette.textSecondary.main,
}));

const MeterValue = styled(Typography)(({ accent }) => ({
  fontSize: 16,
  fontWeight: 600,
  color: accent,
}));

const MeterTrack = styled('div')(({ theme }) => ({
  width: '100%',
  height: 8,
  borderRadius: 100,
  backgroundColor: theme.palette.grey[100],
  overflow: 'hidden',
}));

const MeterFill = styled('div')(({ accent, score }) => ({
  height: '100%',
  width: `${score}%`,
  backgroundColor: accent,
  borderRadius: 100,
  transition: 'width 0.4s ease-out',
}));

const ImpactText = styled(Typography)(() => ({
  fontSize: 16,
  lineHeight: '24px',
  marginBottom: 24,
}));

const ColumnsGrid = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: 24,
  [theme.breakpoints.down('md')]: {
    gridTemplateColumns: '1fr',
    gap: 20,
  },
}));

const Column = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
}));

const ColumnTitle = styled(Typography)(({ theme }) => ({
  fontSize: 18,
  fontWeight: 500,
  marginBottom: 14,
  paddingBottom: 10,
  borderBottom: '1px solid #d6d6d6',
  color: theme.palette.textPrimary.main,
}));

const ItemList = styled('ul')(({ theme }) => ({
  listStyle: 'none',
  padding: 0,
  margin: 0,
  fontFamily: 'Rubik-Regular',
  color: theme.palette.textPrimary.main,
}));

const Item = styled('li')(({ theme }) => ({
  position: 'relative',
  paddingLeft: 30,
  marginBottom: 12,
  fontFamily: 'Rubik-Regular',
  fontSize: 16,
  lineHeight: '24px',
  fontWeight: 400,
  color: theme.palette.textPrimary.main,
  '&:last-child': {
    marginBottom: 0,
  },
  '& svg': {
    position: 'absolute',
    left: 0,
    top: 2,
    width: 20,
    height: 20,
  },
}));

const getLevelKey = (raw) => {
  if (raw == null) return 'medium';
  const num = typeof raw === 'number' ? raw : Number(raw);
  if (Number.isInteger(num) && INT_TO_KEY[num]) return INT_TO_KEY[num];
  const key = String(raw).toLowerCase().trim().replace(/[\s_]+/g, '-');
  if (RISK_LEVELS[key]) return key;
  if (key.startsWith('very')) return 'very-high';
  if (['severe', 'extreme', 'critical'].includes(key)) return 'critical';
  if (['safe', 'none'].includes(key)) return 'minimal';
  return 'medium';
};

const pickLocalized = (field, lang) => {
  if (!field) return null;
  if (typeof field === 'string') return field;
  if (Array.isArray(field)) return field;
  return field[lang] || field[lang?.toUpperCase?.()] || field.en || field.EN || field.ua || null;
};

const PREVIEW_AI_RISK = {
  aiRiskLevel: 'high',
  aiImpact: {
    en: 'Generative AI is rapidly automating routine visual exploration and pattern-driven layout work that has historically taken designers hours per screen. Tools can now produce wireframes, color systems, and copy variants in seconds, compressing the early stages of the design process. Higher-order judgment — research synthesis, stakeholder alignment, and accessible interaction design — remains firmly in human hands. Designers who pair AI fluency with strategic craft will see their leverage grow even as commodity output gets cheaper.',
    ua: 'Генеративний ШІ швидко автоматизує рутинну візуальну роботу та шаблонні макети, які раніше займали години на екран. Інструменти за секунди створюють вайрфрейми, колірні системи та варіанти текстів, скорочуючи ранні етапи дизайн-процесу. Стратегічне мислення — дослідження користувачів, узгодження зі стейкхолдерами, доступна взаємодія — залишається за людиною. Дизайнери, які поєднують володіння ШІ з якісним ремеслом, отримають перевагу.',
  },
  aiTasksAtRisk: {
    en: [
      'Producing first-pass wireframes and layout variations',
      'Generating icon sets, illustrations, and asset variants',
      'Drafting microcopy, error states, and onboarding text',
      'Creating standard component documentation',
    ],
    ua: [
      'Створення перших чернеток вайрфреймів та варіантів макетів',
      'Генерація іконок, ілюстрацій та варіантів ассетів',
      'Написання мікрокопії, текстів помилок та онбордингу',
      'Стандартна документація компонентів',
    ],
  },
  aiTasksSurviving: {
    en: [
      'User research and synthesis of qualitative insights',
      'Cross-functional alignment with PMs, engineers, and execs',
      'Designing for accessibility and edge-case scenarios',
      'Defining product strategy and information architecture',
      'Critique, mentorship, and design-system governance',
    ],
    ua: [
      'Дослідження користувачів та синтез якісних інсайтів',
      'Координація з продакт-менеджерами, інженерами та керівництвом',
      'Дизайн доступності та обробка нестандартних сценаріїв',
      'Стратегія продукту та інформаційна архітектура',
      'Критика, менторство та керування дизайн-системою',
    ],
  },
};

const AiRisk = ({ professionInfo, t }) => {
  const previewMode =
    typeof window !== 'undefined' && /[?&]previewAiRisk=1/.test(window.location.search);
  const aiRisk = professionInfo?.aiRisk || (previewMode ? PREVIEW_AI_RISK : null);
  const lang = getLanguage();

  if (!aiRisk) return null;

  const impact = pickLocalized(aiRisk.aiImpact, lang);
  const tasksAtRisk = pickLocalized(aiRisk.aiTasksAtRisk, lang);
  const tasksSurviving = pickLocalized(aiRisk.aiTasksSurviving, lang);

  const hasContent =
    !!impact ||
    (Array.isArray(tasksAtRisk) && tasksAtRisk.length > 0) ||
    (Array.isArray(tasksSurviving) && tasksSurviving.length > 0);

  if (!hasContent) return null;

  const levelKey = getLevelKey(aiRisk.aiRiskLevel);
  const level = RISK_LEVELS[levelKey];
  const levelLabel = t(`AiRiskLevel_${levelKey}`) || levelKey;

  const showTasksAtRisk = Array.isArray(tasksAtRisk) && tasksAtRisk.length > 0;
  const showTasksSurviving = Array.isArray(tasksSurviving) && tasksSurviving.length > 0;

  return (
    <Section>
      <HeaderRow>
        <HeaderTitle variant="h2">
          {t('AiRiskTitle', professionCases(professionInfo, 'genitive'))}
        </HeaderTitle>
        <RiskPill accent={level.color}>
          <PillDot accent={level.color} />
          {levelLabel}
        </RiskPill>
      </HeaderRow>

      <Content>
        <MeterBlock>
          <MeterLabelRow>
            <MeterLabel variant="body2">{t('AiRiskMeter')}</MeterLabel>
            <MeterValue variant="body2" accent={level.color}>
              {level.score}%
            </MeterValue>
          </MeterLabelRow>
          <MeterTrack>
            <MeterFill accent={level.color} score={level.score} />
          </MeterTrack>
        </MeterBlock>

        {impact ? <ImpactText variant="body1">{impact}</ImpactText> : null}

        {showTasksAtRisk || showTasksSurviving ? (
          <ColumnsGrid>
            {showTasksAtRisk ? (
              <Column>
                <ColumnTitle variant="subtitle1">{t('AiTasksAtRisk')}</ColumnTitle>
                <ItemList>
                  {tasksAtRisk.map((task, idx) => (
                    <Item key={`risk-${idx}`}>
                      <MinusIcon />
                      {typeof task === 'string' ? task : task?.text || task?.name}
                    </Item>
                  ))}
                </ItemList>
              </Column>
            ) : null}
            {showTasksSurviving ? (
              <Column>
                <ColumnTitle variant="subtitle1">{t('AiTasksSurviving')}</ColumnTitle>
                <ItemList>
                  {tasksSurviving.map((task, idx) => (
                    <Item key={`safe-${idx}`}>
                      <PlusIcon />
                      {typeof task === 'string' ? task : task?.text || task?.name}
                    </Item>
                  ))}
                </ItemList>
              </Column>
            ) : null}
          </ColumnsGrid>
        ) : null}
      </Content>
    </Section>
  );
};

export default AiRisk;
